/**
 * Shopify Theme Integration Scripts
 * Auto-implementation for common Shopify events
 */

class ShopifyIntegrations {
  constructor(dataLayer) {
    this.dataLayer = dataLayer;
    this.trackedOrders = new Set();
    this.hasTrackedViewItem = false;
    this.hasTrackedBeginCheckout = false;
    this.hasTrackedThankYouPurchase = false;
    this.init();
  }

  init() {
    this.setupProductPageTracking();
    this.setupCollectionPageTracking();
    this.setupCartTracking();
    this.setupCheckoutTracking();
    this.setupSearchTracking();
    this.setupAccountTracking();
    this.setupAddToCartTracking();
    
    // Only enable aggressive tracking on thank you pages
    if (this.isThankYouPage() || this.isOrderConfirmationPage()) {
      this.setupAggressivePurchaseTracking();
    }
  }

  setupProductPageTracking() {
    // Additional check to ensure we're on an actual product page
    if (this.isProductPage() && this.isActualProductPage()) {
      const product = this.getProductData();
      if (product && product.id) {
        // Only track view item once per page load
        if (!this.hasTrackedViewItem) {
          this.dataLayer.viewItem(product);
          this.hasTrackedViewItem = true;
        }
        this.setupVariantTracking(product);
        this.setupQuantityTracking(product);
      }
    }
  }

  setupCollectionPageTracking() {
    if (this.isCollectionPage()) {
      const products = this.getCollectionProducts();
      const collectionName = this.getCollectionName();
      const collectionId = this.getCollectionId();
      
      if (products.length > 0) {
        this.dataLayer.viewItemList(products, collectionName, collectionId);
      }
    }
  }

  setupCartTracking() {
    // Only track cart on actual cart page
    if (this.isCartPage()) {
      this.loadCartData().then(cart => {
        this.dataLayer.viewCart(cart);
      });
    }

    // Only observe cart drawer on pages where it makes sense (not on cart page itself)
    if (!this.isCartPage()) {
      this.observeCartDrawer();
    }
    
    // Track cart updates everywhere (for add to cart functionality)
    this.trackCartUpdates();
  }

  setupCheckoutTracking() {
    if (this.isCheckoutPage()) {
      // Multiple methods to track begin_checkout in sandbox
      this.trackBeginCheckoutMultiple();
      this.trackCheckoutSteps();
    }
    
    // Track thank you page purchases
    if (this.isThankYouPage()) {
      this.trackThankYouPurchase();
    }
    
    // Enhanced cross-domain tracking
    this.setupCrossDomainTracking();
  }
  
  trackBeginCheckoutMultiple() {
    // Method 1: Try cart API first
    this.loadCartData().then(cart => {
      if (cart && cart.items && cart.items.length > 0) {
        this.dataLayer.beginCheckout(cart);
        this.hasTrackedBeginCheckout = true;
        return;
      }
      
      // Method 2: Fallback to localStorage
      this.tryCheckoutFromStorage();
    }).catch(() => {
      // Method 3: Extract from page DOM
      this.tryCheckoutFromDOM();
    });
    
    // Method 4: Listen for Shopify checkout events
    this.listenForShopifyCheckoutEvents();
  }
  
  tryCheckoutFromStorage() {
    try {
      const storedCart = localStorage.getItem('shopify_checkout_order');
      if (storedCart) {
        const cart = JSON.parse(storedCart);
        if (cart.items && cart.items.length > 0) {
          this.dataLayer.beginCheckout(cart);
          this.hasTrackedBeginCheckout = true;
          return;
        }
      }
    } catch (e) {
      console.warn('Failed to load checkout from storage:', e);
    }
    
    // If localStorage fails, try DOM extraction
    this.tryCheckoutFromDOM();
  }
  
  tryCheckoutFromDOM() {
    setTimeout(() => {
      if (!this.hasTrackedBeginCheckout) {
        this.triggerBeginCheckoutFromPage();
      }
    }, 1000);
  }
  
  listenForShopifyCheckoutEvents() {
    // Listen for window messages from parent domain
    window.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'checkout_data') {
        const cart = event.data.cart;
        if (cart && !this.hasTrackedBeginCheckout) {
          this.dataLayer.beginCheckout(cart);
          this.hasTrackedBeginCheckout = true;
        }
      }
    });
    
    // Listen for Shopify analytics events
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib) {
      const originalTrack = window.ShopifyAnalytics.lib.track;
      window.ShopifyAnalytics.lib.track = (eventName, properties) => {
        if (eventName === 'Checkout Started' && !this.hasTrackedBeginCheckout) {
          this.convertShopifyCheckoutToCart(properties);
        }
        return originalTrack.call(this, eventName, properties);
      };
    }
  }
  
  convertShopifyCheckoutToCart(properties) {
    try {
      const cart = {
        items: properties.lineItems || [],
        total_price: properties.totalPrice || 0,
        currency: properties.currency || this.getCurrency(),
        subtotal_price: properties.subtotalPrice
      };
      
      if (cart.items.length > 0) {
        this.dataLayer.beginCheckout(cart);
        this.hasTrackedBeginCheckout = true;
      }
    } catch (e) {
      console.warn('Failed to convert Shopify checkout data:', e);
    }
  }
  
  setupCrossDomainTracking() {
    // Send cart data to checkout domain if different
    if (window.location.hostname !== 'checkout.shopifycs.com') {
      try {
        const cart = this.getStoredCartData();
        if (cart) {
          // Post message to checkout iframe/window
          const checkoutFrame = document.querySelector('iframe[src*="checkout"]');
          if (checkoutFrame) {
            checkoutFrame.contentWindow.postMessage({
              type: 'checkout_data',
              cart: cart
            }, '*');
          }
        }
      } catch (e) {
        console.warn('Cross-domain tracking setup failed:', e);
      }
    }
  }
  
  getStoredCartData() {
    try {
      const stored = localStorage.getItem('shopify_checkout_order');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }

  setupSearchTracking() {
    if (this.isSearchPage()) {
      const searchTerm = this.getSearchTerm();
      const results = this.getSearchResults();
      this.dataLayer.search(searchTerm, results);
    }

    // Track search form submissions
    this.trackSearchForms();
  }

  setupAccountTracking() {
    this.trackLoginForms();
    this.trackRegistrationForms();
  }

  // Product Page Methods
  isProductPage() {
    const path = window.location.pathname;
    const pageType = window.ShopifyAnalytics?.meta?.page?.pageType;
    const bodyClass = document.body.className;
    
    // Must be exactly a product page, not home page with products
    return (path.includes('/products/') && !path.endsWith('/products')) || 
           bodyClass.includes('template-product') ||
           (pageType === 'product' && path !== '/');
  }
  
  isActualProductPage() {
    // Double check we're really on a product page, not home/collection with product data
    const path = window.location.pathname;
    return path.includes('/products/') && 
           path.split('/').length >= 3 && 
           !path.endsWith('/') &&
           document.querySelector('.product-single, .product-details, [data-product-id]') !== null;
  }

  getProductData() {
    // Try multiple sources for product data
    let product = null;

    // Method 1: Shopify Analytics
    if (window.ShopifyAnalytics?.meta?.product) {
      product = window.ShopifyAnalytics.meta.product;
    }

    // Method 2: JSON-LD structured data
    if (!product) {
      const jsonLd = document.querySelector('script[type="application/ld+json"]');
      if (jsonLd) {
        try {
          const data = JSON.parse(jsonLd.textContent);
          if (data['@type'] === 'Product') {
            product = this.parseJSONLDProduct(data);
          }
        } catch (e) {}
      }
    }

    // Method 3: Meta tags
    if (!product) {
      product = this.parseMetaProduct();
    }

    // Method 4: DOM scraping
    if (!product) {
      product = this.parseProductFromDOM();
    }

    return product;
  }

  setupVariantTracking(product) {
    const variantSelectors = document.querySelectorAll('input[name="id"], select[name="id"]');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', (e) => {
        const variantId = e.target.value;
        const variant = this.getVariantById(variantId);
        if (variant) {
          const productWithVariant = {
            ...product,
            id: variantId,
            variant_title: variant.title,
            price: variant.price / 100
          };
          this.dataLayer.viewItem(productWithVariant);
        }
      });
    });
  }

  setupQuantityTracking(product) {
    const quantityInputs = document.querySelectorAll('input[name="quantity"]');
    quantityInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        const quantity = parseInt(e.target.value);
        this.dataLayer.customEvent('quantity_changed', {
          product_id: product.id,
          quantity: quantity
        });
      });
    });
  }

  // Collection Page Methods
  isCollectionPage() {
    return window.location.pathname.includes('/collections/') || 
           document.body.classList.contains('template-collection') ||
           window.ShopifyAnalytics?.meta?.page?.pageType === 'collection';
  }

  getCollectionProducts() {
    const products = [];
    const productElements = document.querySelectorAll('[data-product-id], .product-item, .grid-product');
    
    productElements.forEach((element, index) => {
      const product = this.parseProductFromElement(element, index);
      if (product) {
        products.push(product);
      }
    });

    return products;
  }

  getCollectionName() {
    return document.querySelector('h1')?.textContent?.trim() ||
           document.querySelector('.collection-title')?.textContent?.trim() ||
           window.ShopifyAnalytics?.meta?.page?.collectionTitle ||
           'Unknown Collection';
  }

  getCollectionId() {
    return window.ShopifyAnalytics?.meta?.page?.collectionId ||
           document.querySelector('[data-collection-id]')?.dataset.collectionId ||
           null;
  }

  // Cart Methods
  isCartPage() {
    return window.location.pathname.includes('/cart') ||
           document.body.classList.contains('template-cart');
  }

  async loadCartData() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Store cart data for potential purchase tracking
      if (cart && cart.items && cart.items.length > 0) {
        this.storeCheckoutData(cart);
      }
      
      return cart;
    } catch (error) {
      console.error('Failed to load cart data:', error);
      return { items: [], total_price: 0 };
    }
  }
  
  storeCheckoutData(cartData) {
    // Store cart/checkout data in localStorage for purchase tracking
    try {
      const checkoutData = {
        items: cartData.items,
        total_price: cartData.total_price,
        currency: cartData.currency,
        timestamp: Date.now()
      };
      localStorage.setItem('shopify_checkout_order', JSON.stringify(checkoutData));
    } catch (e) {
      console.warn('Failed to store checkout data:', e);
    }
  }

  observeCartDrawer() {
    let lastCartDrawerState = false;
    
    // Check for cart drawer opening via click events (more reliable)
    document.addEventListener('click', (e) => {
      const cartTrigger = e.target.closest('[data-cart-drawer-toggle], .cart-link, .cart-toggle, [href*="cart"]');
      if (cartTrigger) {
        setTimeout(() => {
          const cartDrawer = document.querySelector('.cart-drawer, #cart-drawer, [data-cart-drawer]');
          if (cartDrawer && this.isCartDrawerVisible(cartDrawer)) {
            this.loadCartData().then(cart => {
              if (cart && cart.items && cart.items.length > 0) {
                this.dataLayer.viewCart(cart);
              }
            });
          }
        }, 500); // Give drawer time to open
      }
    });
    
    // Also observe for drawer state changes, but more carefully
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const cartDrawer = document.querySelector('.cart-drawer, #cart-drawer, [data-cart-drawer]');
          if (cartDrawer) {
            const isVisible = this.isCartDrawerVisible(cartDrawer);
            if (isVisible && !lastCartDrawerState) {
              // Drawer just opened
              this.loadCartData().then(cart => {
                if (cart && cart.items && cart.items.length > 0) {
                  this.dataLayer.viewCart(cart);
                }
              });
            }
            lastCartDrawerState = isVisible;
          }
        }
      });
    });

    // Only observe the cart drawer element itself, not the whole body
    const cartDrawer = document.querySelector('.cart-drawer, #cart-drawer, [data-cart-drawer]');
    if (cartDrawer) {
      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }
  }
  
  isCartDrawerVisible(drawer) {
    if (!drawer) return false;
    
    const style = window.getComputedStyle(drawer);
    const hasVisibleClass = drawer.classList.contains('open') || 
                           drawer.classList.contains('active') || 
                           drawer.classList.contains('is-open');
    const hasVisibleStyle = style.display !== 'none' && 
                           style.visibility !== 'hidden' && 
                           style.opacity !== '0';
    
    return hasVisibleClass || hasVisibleStyle;
  }

  trackCartUpdates() {
    // Override cart update functions
    if (window.CartJS) {
      const originalAdd = window.CartJS.addItem;
      window.CartJS.addItem = function(id, quantity, properties, callback) {
        const result = originalAdd.call(this, id, quantity, properties, callback);
        window.shopifyDataLayer?.customEvent('cart_item_added', { variant_id: id, quantity });
        return result;
      };
    }

    // Track quantity changes in cart
    document.addEventListener('change', (e) => {
      if (e.target.name === 'updates[]' || e.target.classList.contains('cart-quantity')) {
        const newQuantity = parseInt(e.target.value);
        const oldQuantity = parseInt(e.target.defaultValue);
        const variantId = e.target.dataset.variantId;
        
        if (newQuantity > oldQuantity) {
          this.dataLayer.customEvent('cart_quantity_increased', {
            variant_id: variantId,
            old_quantity: oldQuantity,
            new_quantity: newQuantity
          });
        } else if (newQuantity < oldQuantity) {
          this.dataLayer.customEvent('cart_quantity_decreased', {
            variant_id: variantId,
            old_quantity: oldQuantity,
            new_quantity: newQuantity
          });
        }
      }
    });
  }

  // Checkout Methods
  isCheckoutPage() {
    return window.location.pathname.includes('/checkout') ||
           window.Shopify?.Checkout ||
           document.body.classList.contains('checkout');
  }

  trackCheckoutSteps() {
    // Shopify Plus checkout events
    if (window.Shopify?.Checkout) {
      window.Shopify.Checkout.addEventListener('page:change', (e) => {
        this.handleCheckoutStep(e.detail.step);
      });

      window.Shopify.Checkout.addEventListener('order:status_update', (e) => {
        if (e.detail.status === 'complete') {
          this.trackComprehensivePurchase(e.detail.order);
        }
      });
    }

    // Standard checkout step tracking
    this.observeCheckoutSteps();
    
    // Listen for checkout completion events
    this.observeCheckoutCompletion();
  }

  observeCheckoutSteps() {
    const stepIndicators = document.querySelectorAll('.breadcrumb__item, .step');
    stepIndicators.forEach((step, index) => {
      if (step.classList.contains('breadcrumb__item--current') || 
          step.classList.contains('step--current')) {
        this.dataLayer.customEvent('checkout_progress', {
          checkout_step: index + 1,
          checkout_option: step.textContent.trim()
        });
        
        // Also trigger begin_checkout on the first step if not already done
        if ((index === 0 || step.textContent.toLowerCase().includes('information')) && 
            !this.hasTrackedBeginCheckout) {
          this.triggerBeginCheckoutFromPage();
        }
      }
    });
  }
  
  triggerBeginCheckoutFromPage() {
    // Try to construct checkout data from page elements
    const checkoutItems = this.extractCheckoutItemsFromPage();
    if (checkoutItems.length > 0) {
      const cart = {
        items: checkoutItems,
        total_price: this.extractTotalFromPage(),
        currency: this.getCurrency()
      };
      this.dataLayer.beginCheckout(cart);
      this.hasTrackedBeginCheckout = true;
    }
  }
  
  extractCheckoutItemsFromPage() {
    const items = [];
    // Look for common checkout item selectors
    const itemElements = document.querySelectorAll(
      '.product, .line-item, .checkout-product, [data-variant-id], .order-summary__section--product-list .product'
    );
    
    itemElements.forEach((element, index) => {
      const item = {
        id: element.getAttribute('data-variant-id') || 
            element.getAttribute('data-product-id') || 
            `checkout-item-${index}`,
        title: this.extractTextFromElement(element, '.product-title, .product__title, h3, .product-name'),
        price: this.extractPriceFromElement(element),
        quantity: this.extractQuantityFromElement(element),
        variant_title: this.extractTextFromElement(element, '.variant, .product-variant, .variant-title'),
        vendor: this.extractTextFromElement(element, '.vendor, .product-vendor, .brand')
      };
      if (item.title) items.push(item);
    });
    
    return items;
  }
  
  extractTotalFromPage() {
    const totalElements = document.querySelectorAll('.total-price, .checkout-total, .order-summary-total, [data-checkout-total]');
    for (const element of totalElements) {
      const price = this.extractPriceFromText(element.textContent);
      if (price > 0) return price;
    }
    return 0;
  }
  
  extractTextFromElement(container, selector) {
    const element = container.querySelector(selector);
    return element ? element.textContent.trim() : '';
  }
  
  extractPriceFromElement(element) {
    const priceElements = element.querySelectorAll('.price, .product-price, .money, [data-price]');
    for (const priceEl of priceElements) {
      const price = this.extractPriceFromText(priceEl.textContent);
      if (price > 0) return price;
    }
    return 0;
  }
  
  extractQuantityFromElement(element) {
    const qtyElements = element.querySelectorAll('.quantity, .qty, .product-quantity, [data-quantity]');
    for (const qtyEl of qtyElements) {
      const qty = parseInt(qtyEl.textContent) || parseInt(qtyEl.value) || 1;
      if (qty > 0) return qty;
    }
    return 1;
  }
  
  extractPriceFromText(text) {
    const match = text.match(/[\d.,]+/);
    return match ? parseFloat(match[0].replace(/,/g, '')) : 0;
  }
  
  // Enhanced Thank You Page Methods
  observeThankYouPageChanges() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(() => {
        if (document.querySelector('.order-number, .order-id, [data-order-id]') && 
            !this.hasTrackedThankYouPurchase) {
          setTimeout(() => this.captureOrderDataFromPage(), 200);
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });
    
    setTimeout(() => observer.disconnect(), 10000);
  }
  
  extractOrderFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const orderId = urlParams.get('order_id') || 
                   urlParams.get('order') || 
                   window.location.pathname.match(/orders\/(\d+)/)?.[1];
    
    if (orderId && !this.hasTrackedThankYouPurchase) {
      console.log('[Shopify] Found order ID in URL:', orderId);
      this.buildOrderFromOrderId(orderId);
    }
  }
  
  buildOrderFromOrderId(orderId) {
    const orderData = {
      transaction_id: orderId,
      id: orderId,
      order_number: orderId,
      items: this.extractItemsFromThankYouPage(),
      total_price: this.extractTotalFromThankYouPage(),
      currency: this.getCurrency(),
      customer_email: this.extractCustomerEmail(),
      created_at: new Date().toISOString()
    };
    
    if (orderData.items.length > 0 || orderData.total_price > 0) {
      console.log('[Shopify] Built order from thank you page:', orderData);
      this.trackComprehensivePurchase(orderData);
    }
  }
  
  extractOrderFromPageScripts() {
    const scripts = document.querySelectorAll('script');
    scripts.forEach(script => {
      const content = script.textContent || script.innerText;
      if (content.includes('order') || content.includes('checkout')) {
        try {
          const jsonMatch = content.match(/\{[^}]*"order"[^}]*\}/g) ||
                           content.match(/\{[^}]*"id":\s*\d+[^}]*\}/g);
          
          if (jsonMatch) {
            jsonMatch.forEach(match => {
              try {
                const data = JSON.parse(match);
                if (data.order || data.id || data.line_items) {
                  console.log('[Shopify] Found order data in script:', data);
                  this.trackComprehensivePurchase(data.order || data);
                }
              } catch (e) {
                const orderIdMatch = match.match(/"id":\s*(\d+)/);
                if (orderIdMatch) {
                  this.buildOrderFromOrderId(orderIdMatch[1]);
                }
              }
            });
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }
    });
  }
  
  extractItemsFromThankYouPage() {
    const items = [];
    
    const itemSelectors = [
      '.product-line-item, .line-item',
      '.order-summary-item, .order-item',
      '.product-row, .purchased-product',
      '[data-product-id], [data-variant-id]',
      '.thank-you-product, .confirmation-product'
    ];
    
    itemSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element, index) => {
        const item = this.extractItemFromElement(element, index);
        if (item && item.title) {
          items.push(item);
        }
      });
    });
    
    return items;
  }
  
  extractItemFromElement(element, index) {
    return {
      id: element.getAttribute('data-variant-id') || 
          element.getAttribute('data-product-id') || 
          `thankyou-item-${index}`,
      title: this.extractTextFromElement(element, '.product-title, .product-name, .item-title, h3, h4, .name') ||
             this.extractTextFromElement(element, 'img').replace(/.*alt="([^"]*)".*/, '$1') ||
             `Product ${index + 1}`,
      price: this.extractPriceFromElement(element),
      quantity: this.extractQuantityFromElement(element),
      variant_title: this.extractTextFromElement(element, '.variant, .variant-title, .product-variant'),
      vendor: this.extractTextFromElement(element, '.vendor, .brand, .product-vendor'),
      product_type: this.extractTextFromElement(element, '.product-type, .category')
    };
  }
  
  extractTotalFromThankYouPage() {
    const totalSelectors = [
      '.total-price, .order-total',
      '.grand-total, .final-total',
      '.payment-due, .amount-due',
      '[data-total], [data-order-total]'
    ];
    
    for (const selector of totalSelectors) {
      const elements = document.querySelectorAll(selector);
      for (const element of elements) {
        const price = this.extractPriceFromText(element.textContent);
        if (price > 0) {
          return price;
        }
      }
    }
    return 0;
  }
  
  extractCustomerEmail() {
    const emailSelectors = [
      '.customer-email, .email',
      '[data-email]',
      'input[type="email"]'
    ];
    
    for (const selector of emailSelectors) {
      const element = document.querySelector(selector);
      if (element) {
        return element.textContent || element.value || element.getAttribute('data-email');
      }
    }
    return null;
  }
  
  manualOrderExtraction() {
    if (!this.hasTrackedThankYouPurchase) {
      console.log('[Shopify] Manual order extraction as final fallback...');
      
      const orderData = {
        transaction_id: this.generateFallbackOrderId(),
        items: this.extractItemsFromThankYouPage(),
        total_price: this.extractTotalFromThankYouPage(),
        currency: this.getCurrency(),
        created_at: new Date().toISOString(),
        fallback_tracking: true
      };
      
      if (orderData.items.length > 0 || orderData.total_price > 0) {
        console.log('[Shopify] Manual extraction successful:', orderData);
        this.trackComprehensivePurchase(orderData);
      } else {
        console.warn('[Shopify] Could not extract any order data from thank you page');
      }
    }
  }
  
  generateFallbackOrderId() {
    const pageText = document.body.textContent;
    const numberMatch = pageText.match(/order\s*#?\s*(\d{4,})/i) ||
                       pageText.match(/#(\d{4,})/);
    
    return numberMatch ? numberMatch[1] : `fallback-${Date.now()}`;
  }
  
  monitorStorageForOrder() {
    const checkStorage = () => {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if ((key.includes('order') || key.includes('checkout')) && 
            !key.includes('cart') && !this.hasTrackedThankYouPurchase) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            if (data && (data.line_items || data.items || data.id)) {
              console.log('[Shopify] Found order in localStorage:', key, data);
              this.trackComprehensivePurchase(data);
            }
          } catch (e) {
            // Ignore parsing errors
          }
        }
      });
    };
    
    for (let i = 1; i <= 5; i++) {
      setTimeout(checkStorage, i * 1000);
    }
  }

  // Search Methods
  isSearchPage() {
    return window.location.pathname.includes('/search') ||
           document.body.classList.contains('template-search') ||
           new URLSearchParams(window.location.search).has('q');
  }

  getSearchTerm() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('q') || urlParams.get('query') || '';
  }

  getSearchResults() {
    const resultElements = document.querySelectorAll('.search-result, .product-item');
    return Array.from(resultElements).map(element => this.parseProductFromElement(element));
  }

  trackSearchForms() {
    const searchForms = document.querySelectorAll('form[action*="/search"], .search-form');
    searchForms.forEach(form => {
      form.addEventListener('submit', () => {
        const searchInput = form.querySelector('input[name="q"], input[name="query"]');
        if (searchInput) {
          this.dataLayer.customEvent('search_initiated', {
            search_term: searchInput.value
          });
        }
      });
    });
  }

  // Add to Cart Tracking
  setupAddToCartTracking() {
    // Global flag to prevent multiple tracking methods from firing
    this.ajaxAddToCartActive = false;
    this.lastAddToCartTrackTime = 0;
    
    // Always try AJAX interception first (most reliable)
    this.interceptAddToCartRequests();
    
    // Only setup form fallback if AJAX isn't working
    setTimeout(() => {
      if (!this.ajaxAddToCartActive) {
        this.setupFormBasedAddToCart();
      }
    }, 2000); // Increased delay to ensure AJAX detection
  }
  
  handleAddToCartForm(form) {
    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity')) || 1;
    
    if (variantId) {
      const product = this.getProductData();
      if (product) {
        const variant = this.getVariantById(variantId);
        this.dataLayer.addToCart({
          ...product,
          id: variantId,
          variant_title: variant?.title || product.variant_title,
          price: variant?.price / 100 || product.price,
          quantity: quantity
        });
        
        // Store product details for purchase tracking
        this.storeProductForPurchaseTracking(product, variantId, quantity);
      }
    }
  }
  
  handleAddToCartButton(button) {
    const productContainer = button.closest('[data-product-id], .product-item, .grid-product');
    if (productContainer) {
      const product = this.parseProductFromElement(productContainer);
      if (product) {
        this.dataLayer.addToCart(product);
        this.storeProductForPurchaseTracking(product, product.id, product.quantity || 1);
      }
    }
  }
  
  interceptAddToCartRequests() {
    const self = this;
    
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      const [resource, config] = args;
      
      if (typeof resource === 'string' && resource.includes('/cart/add')) {
        // Mark AJAX as active
        self.ajaxAddToCartActive = true;
        
        // Prevent rapid duplicate requests (within 3 seconds)
        const now = Date.now();
        if (now - self.lastAddToCartTrackTime < 3000) {
          return originalFetch.apply(this, args);
        }
        
        try {
          const response = await originalFetch.apply(this, args);
          
          // Only track if response is successful
          if (response.ok) {
            self.lastAddToCartTrackTime = now;
            
            // Try to extract product data from the request
            if (config && config.body) {
              let productData = {};
              if (config.body instanceof FormData) {
                productData.id = config.body.get('id');
                productData.quantity = parseInt(config.body.get('quantity')) || 1;
              } else if (typeof config.body === 'string') {
                try {
                  const parsedBody = JSON.parse(config.body);
                  productData = parsedBody;
                } catch (e) {}
              }
              
              if (productData.id) {
                const currentProduct = self.getProductData();
                if (currentProduct) {
                  const variant = self.getVariantById(productData.id);
                  const trackingData = {
                    ...currentProduct,
                    id: productData.id,
                    variant_title: variant?.title || currentProduct.variant_title,
                    price: variant?.price / 100 || currentProduct.price,
                    quantity: productData.quantity || 1
                  };
                  
                  // Small delay to ensure proper sequencing
                  setTimeout(() => {
                    window.shopifyDataLayer?.addToCart(trackingData);
                    self.storeProductForPurchaseTracking(trackingData, productData.id, productData.quantity || 1);
                  }, 200);
                }
              }
            }
          }
          
          return response;
        } catch (error) {
          return originalFetch.apply(this, args);
        }
      }
      
      return originalFetch.apply(this, args);
    };
    
    // Also intercept XMLHttpRequest
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;
    
    XMLHttpRequest.prototype.open = function(method, url, ...args) {
      this._url = url;
      this._method = method;
      return originalXHROpen.call(this, method, url, ...args);
    };
    XMLHttpRequest.prototype.send = function(data) {
      if (this._url && this._url.includes('/cart/add')) {
        self.ajaxAddToCartActive = true;
        
        this.addEventListener('load', function() {
          if (this.status >= 200 && this.status < 300) {
            const now = Date.now();
            if (now - self.lastAddToCartTrackTime >= 3000) {
              self.lastAddToCartTrackTime = now;
              
              // Try to extract data from XHR request
              let productData = {};
              if (data instanceof FormData) {
                productData.id = data.get('id');
                productData.quantity = parseInt(data.get('quantity')) || 1;
              }
              
              if (productData.id) {
                const currentProduct = self.getProductData();
                if (currentProduct) {
                  const variant = self.getVariantById(productData.id);
                  const trackingData = {
                    ...currentProduct,
                    id: productData.id,
                    variant_title: variant?.title || currentProduct.variant_title,
                    price: variant?.price / 100 || currentProduct.price,
                    quantity: productData.quantity || 1
                  };
                  
                  setTimeout(() => {
                    window.shopifyDataLayer?.addToCart(trackingData);
                    self.storeProductForPurchaseTracking(trackingData, productData.id, productData.quantity || 1);
                  }, 200);
                }
              }
            }
          }
        });
      }
      
      return originalXHRSend.call(this, data);
    };
  }
  
  setupFormBasedAddToCart() {
    const self = this;
    
    // Only setup form tracking if AJAX isn't active
    document.addEventListener('submit', (e) => {
      const form = e.target;
      
      // Skip if AJAX is handling add to cart
      if (self.ajaxAddToCartActive) {
        return;
      }
      
      if (form.action && (form.action.includes('/cart/add') || form.querySelector('input[name="add"]'))) {
        // Prevent rapid duplicate form submissions
        const now = Date.now();
        if (now - self.lastAddToCartTrackTime < 3000) {
          return;
        }
        self.lastAddToCartTrackTime = now;
        
        // Add delay to avoid race conditions with potential AJAX
        setTimeout(() => {
          if (!self.ajaxAddToCartActive) {
            self.handleAddToCartForm(form);
          }
        }, 100);
      }
    });
  }
  
  storeProductForPurchaseTracking(product, variantId, quantity) {
    try {
      const existingData = JSON.parse(localStorage.getItem('shopify_tracked_products') || '[]');
      existingData.push({
        ...product,
        variant_id: variantId,
        quantity: quantity,
        timestamp: Date.now()
      });
      
      // Keep only last 50 products to avoid localStorage bloat
      if (existingData.length > 50) {
        existingData.splice(0, existingData.length - 50);
      }
      
      localStorage.setItem('shopify_tracked_products', JSON.stringify(existingData));
    } catch (e) {
      console.warn('Failed to store product tracking data:', e);
    }
  }

  // Account Methods
  trackLoginForms() {
    const loginForms = document.querySelectorAll('form[action*="/account/login"], .login-form');
    loginForms.forEach(form => {
      form.addEventListener('submit', () => {
        this.dataLayer.login('email');
      });
    });
  }

  trackRegistrationForms() {
    const registerForms = document.querySelectorAll('form[action*="/account"], .register-form');
    registerForms.forEach(form => {
      form.addEventListener('submit', () => {
        if (form.action.includes('register') || form.querySelector('input[name="customer[password]"]')) {
          this.dataLayer.signUp('email');
        }
      });
    });
  }

  // Helper Methods
  parseJSONLDProduct(data) {
    const offer = data.offers?.[0] || data.offers;
    return {
      id: data.productID || data.sku,
      title: data.name,
      price: offer?.price || 0,
      vendor: data.brand?.name || data.manufacturer?.name,
      type: data.category,
      variant_title: data.model || data.name
    };
  }

  parseMetaProduct() {
    return {
      id: document.querySelector('meta[property="product:retailer_item_id"]')?.content,
      title: document.querySelector('meta[property="og:title"]')?.content,
      price: parseFloat(document.querySelector('meta[property="product:price:amount"]')?.content || 0),
      vendor: document.querySelector('meta[property="product:brand"]')?.content,
      type: document.querySelector('meta[property="product:category"]')?.content
    };
  }

  parseProductFromDOM() {
    const priceElement = document.querySelector('.price, .product-price, [data-price]');
    const titleElement = document.querySelector('h1, .product-title, [data-product-title]');
    
    return {
      id: document.querySelector('[data-product-id]')?.dataset.productId,
      title: titleElement?.textContent?.trim(),
      price: this.extractPriceFromText(priceElement?.textContent),
      vendor: document.querySelector('.vendor, .product-vendor')?.textContent?.trim(),
      type: document.querySelector('.product-type')?.textContent?.trim()
    };
  }

  parseProductFromElement(element, index = 0) {
    const productId = element.dataset.productId ||
                     element.querySelector('[data-product-id]')?.dataset.productId;
    const title = element.querySelector('.product-title, h3, .title')?.textContent?.trim();
    const priceElement = element.querySelector('.price, .product-price');
    const price = this.extractPriceFromText(priceElement?.textContent);
    
    return {
      id: productId,
      title: title,
      price: price,
      vendor: element.querySelector('.vendor')?.textContent?.trim(),
      type: element.querySelector('.product-type')?.textContent?.trim(),
      position: index + 1
    };
  }

  extractPriceFromText(text) {
    if (!text) return 0;
    const match = text.match(/[\d,]+\.?\d*/);
    return match ? parseFloat(match[0].replace(',', '')) : 0;
  }

  getVariantById(variantId) {
    if (window.ShopifyAnalytics?.meta?.product?.variants) {
      return window.ShopifyAnalytics.meta.product.variants.find(v => v.id == variantId);
    }
    return null;
  }

  handleCheckoutStep(step) {
    const stepMap = {
      'contact_information': 1,
      'shipping_method': 2,
      'payment_method': 3,
      'review': 4
    };

    this.dataLayer.customEvent('checkout_progress', {
      checkout_step: stepMap[step] || 1,
      checkout_option: step
    });
  }

  trackPurchase(order) {
    this.trackComprehensivePurchase(order);
  }
  
  trackComprehensivePurchase(orderData) {
    // Enhanced purchase tracking with complete order details
    const enhancedOrder = this.buildComprehensiveOrderData(orderData);
    
    if (enhancedOrder && enhancedOrder.items && enhancedOrder.items.length > 0) {
      // Create unique ID to prevent duplicate tracking
      const orderId = this.createOrderId(enhancedOrder);
      
      if (this.trackedOrders.has(orderId)) {
        this.log(`Order ${orderId} already tracked, skipping duplicate`);
        return;
      }
      
      this.trackedOrders.add(orderId);
      this.dataLayer.purchase(enhancedOrder);
      
      console.log('Purchase tracked successfully:', enhancedOrder);
    } else {
      console.warn('Purchase tracking failed: Invalid or incomplete order data', orderData);
    }
  }
  
  buildComprehensiveOrderData(orderData) {
    // Try multiple data sources to build complete order information
    let order = this.normalizeOrderData(orderData);
    
    // If order data is incomplete, try to supplement from other sources
    if (!order || !order.items || order.items.length === 0) {
      order = this.getOrderFromShopifyAnalytics() || order;
    }
    
    if (!order || !order.items || order.items.length === 0) {
      order = this.getOrderFromThankYouPage() || order;
    }
    
    if (!order || !order.items || order.items.length === 0) {
      order = this.getOrderFromLocalStorage() || order;
    }
    
    // Ensure all required fields are present and properly formatted
    return this.validateAndEnhanceOrder(order);
  }
  
  normalizeOrderData(data) {
    if (!data) return null;
    
    // Handle different order data formats
    const order = {
      transaction_id: data.id || data.order_number || data.checkout_id || Date.now().toString(),
      affiliation: data.shop?.name || window.Shopify?.shop || 'Shopify Store',
      value: this.parsePrice(data.total_price || data.subtotal_price || data.current_total_price || 0),
      tax: this.parsePrice(data.total_tax || data.tax_price || 0),
      shipping: this.parsePrice(data.total_shipping || data.shipping_price || 0),
      currency: data.currency || data.presentment_currency || window.Shopify?.currency?.active || 'USD',
      coupon: this.extractCoupons(data.discount_codes || data.discounts),
      items: this.normalizeOrderItems(data.line_items || data.items || [])
    };
    
    // Add customer information if available
    if (data.customer || data.billing_address) {
      order.customer_data = {
        customer_id: data.customer?.id,
        email: data.customer?.email || data.email,
        phone: data.customer?.phone || data.phone,
        city: data.billing_address?.city || data.customer?.default_address?.city,
        state: data.billing_address?.province || data.customer?.default_address?.province,
        country: data.billing_address?.country || data.customer?.default_address?.country,
        postal_code: data.billing_address?.zip || data.customer?.default_address?.zip
      };
    }
    
    return order;
  }
  
  normalizeOrderItems(items) {
    if (!Array.isArray(items)) return [];
    
    return items.map((item, index) => {
      return {
        item_id: item.id || item.variant_id || item.product_id,
        item_name: item.title || item.product_title || item.name,
        item_category: item.product_type || item.type,
        item_category2: item.vendor || item.brand,
        item_variant: item.variant_title || item.variant,
        item_brand: item.vendor || item.brand,
        price: this.parsePrice(item.price || item.final_price || item.line_price || 0),
        quantity: parseInt(item.quantity) || 1,
        index: index,
        sku: item.sku,
        product_id: item.product_id,
        variant_id: item.variant_id || item.id,
        url: item.url,
        image: item.image,
        handle: item.handle,
        grams: item.grams,
        compare_at_price: this.parsePrice(item.compare_at_price || 0)
      };
    });
  }
  
  extractCoupons(discounts) {
    if (!discounts || !Array.isArray(discounts)) return '';
    return discounts.map(d => d.code || d.title).filter(Boolean).join(', ');
  }
  
  parsePrice(price) {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
      // Handle price in cents (Shopify format)
      const numPrice = parseFloat(price);
      return numPrice > 1000 ? numPrice / 100 : numPrice;
    }
    return 0;
  }
  
  getOrderFromShopifyAnalytics() {
    if (window.ShopifyAnalytics?.meta?.order) {
      return this.normalizeOrderData(window.ShopifyAnalytics.meta.order);
    }
    return null;
  }
  
  getOrderFromThankYouPage() {
    // Try to extract order data from thank you page
    const orderData = {};
    
    // Get order number
    const orderNumberEl = document.querySelector('.order-number, [data-order-number], .checkout__order-number');
    if (orderNumberEl) {
      orderData.id = orderNumberEl.textContent.replace(/\D/g, '');
    }
    
    // Get total price
    const totalEl = document.querySelector('.total-price, .order-total, [data-order-total]');
    if (totalEl) {
      orderData.total_price = this.extractPriceFromText(totalEl.textContent);
    }
    
    // Get order items from page
    const itemElements = document.querySelectorAll('.order-item, .line-item, [data-line-item]');
    if (itemElements.length > 0) {
      orderData.items = Array.from(itemElements).map(el => {
        return {
          title: el.querySelector('.item-title, .product-title')?.textContent?.trim(),
          price: this.extractPriceFromText(el.querySelector('.item-price, .line-price')?.textContent || '0'),
          quantity: parseInt(el.querySelector('.item-quantity')?.textContent || '1'),
          variant_title: el.querySelector('.variant-title')?.textContent?.trim()
        };
      });
    }
    
    return Object.keys(orderData).length > 0 ? this.normalizeOrderData(orderData) : null;
  }
  
  getOrderFromLocalStorage() {
    // Try to get order data from localStorage (backup method)
    try {
      const storedOrder = localStorage.getItem('shopify_checkout_order');
      if (storedOrder) {
        return this.normalizeOrderData(JSON.parse(storedOrder));
      }
    } catch (e) {
      console.warn('Failed to parse stored order data:', e);
    }
    return null;
  }
  
  validateAndEnhanceOrder(order) {
    if (!order) return null;
    
    // Ensure minimum required data
    if (!order.transaction_id) {
      order.transaction_id = 'unknown_' + Date.now();
    }
    
    if (!order.items || order.items.length === 0) {
      console.warn('Order has no items');
      return null;
    }
    
    // Calculate totals if missing
    if (!order.value || order.value === 0) {
      order.value = order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    }
    
    // Ensure currency is set
    if (!order.currency) {
      order.currency = window.Shopify?.currency?.active || 'USD';
    }
    
    // Add timestamp
    order.timestamp = new Date().toISOString();
    
    return order;
  }
  
  isThankYouPage() {
    const path = window.location.pathname;
    return (path.includes('/thank') && path.includes('you')) ||
           (path.includes('/orders/') && !path.includes('/account/orders')) ||
           document.body.classList.contains('template-checkout-thank-you') ||
           document.querySelector('.checkout-thank-you, .order-confirmation, .thank-you-page') !== null;
  }
  
  trackThankYouPurchase() {
    console.log('[Shopify] Thank you page detected, starting purchase tracking...');
    
    // Enhanced tracking with multiple attempts and methods
    this.enhancedThankYouTracking();
    
    // Original method for compatibility
    setTimeout(() => {
      const orderData = this.getOrderFromThankYouPage();
      if (orderData) {
        this.trackComprehensivePurchase(orderData);
      }
    }, 1000);
    
    // Also try immediately in case data is already available
    const immediateOrderData = this.getOrderFromThankYouPage();
    if (immediateOrderData) {
      this.trackComprehensivePurchase(immediateOrderData);
    }
  }
  
  enhancedThankYouTracking() {
    // Multiple delayed attempts for sandbox environments
    setTimeout(() => this.captureOrderDataFromPage(), 100);
    setTimeout(() => this.captureOrderDataFromPage(), 500);
    setTimeout(() => this.captureOrderDataFromPage(), 1500);
    setTimeout(() => this.captureOrderDataFromPage(), 3000);
    
    // Method 1: Watch for DOM changes (order data loading)
    this.observeThankYouPageChanges();
    
    // Method 2: Extract from URL parameters
    this.extractOrderFromURL();
    
    // Method 3: Look for order data in page scripts
    this.extractOrderFromPageScripts();
    
    // Method 4: Monitor localStorage changes
    this.monitorStorageForOrder();
    
    // Method 5: Backup manual extraction
    setTimeout(() => this.manualOrderExtraction(), 5000);
  }
  
  observeCheckoutCompletion() {
    // Listen for URL changes that might indicate order completion
    let lastUrl = location.href;
    new MutationObserver(() => {
      const url = location.href;
      if (url !== lastUrl) {
        lastUrl = url;
        if (this.isThankYouPage()) {
          setTimeout(() => this.trackThankYouPurchase(), 500);
        }
      }
    }).observe(document, {subtree: true, childList: true});
    
    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', () => {
      if (this.isThankYouPage()) {
        setTimeout(() => this.trackThankYouPurchase(), 500);
      }
    });
  }
  
  // Aggressive Purchase Tracking - Multiple Fallback Methods
  setupAggressivePurchaseTracking() {
    // Only use essential tracking methods to prevent loops
    
    // Method 1: Intercept network requests for order data
    this.interceptAllNetworkRequests();
    
    // Method 2: Monitor localStorage changes for order data
    this.monitorLocalStorageChanges();
    
    // Method 3: Limited monitoring (only run for 30 seconds max)
    this.startLimitedPurchaseMonitoring();
  }
  
  interceptAllNetworkRequests() {
    // Intercept XMLHttpRequest
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;
    
    XMLHttpRequest.prototype.open = function(method, url, ...args) {
      this._url = url;
      this._method = method;
      return originalXHROpen.call(this, method, url, ...args);
    };
    
    XMLHttpRequest.prototype.send = function(data) {
      this.addEventListener('load', function() {
        if (this.status >= 200 && this.status < 300) {
          this.checkForOrderData(this._url, this.responseText, data);
        }
      }.bind(this));
      
      return originalXHRSend.call(this, data);
    };
    
    XMLHttpRequest.prototype.checkForOrderData = function(url, response) {
      try {
        // Check if this might be order-related (but not cart-related)
        if (url && (url.includes('checkout') || url.includes('order') || url.includes('purchase')) && !url.includes('/cart/add') && !url.includes('/cart/update') && !url.includes('/cart.js')) {
          let orderData = null;
          
          try {
            const parsedResponse = JSON.parse(response);
            if (parsedResponse && (parsedResponse.order || parsedResponse.checkout || parsedResponse.line_items || parsedResponse.items)) {
              orderData = parsedResponse.order || parsedResponse.checkout || parsedResponse;
            }
          } catch (e) {}
          
          if (orderData) {
            window.shopifyIntegrations?.trackComprehensivePurchase(orderData);
          }
        }
      } catch (e) {
        console.warn('Error checking XHR for order data:', e);
      }
    };
    
    // Intercept fetch requests (already done but enhance)
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      const response = await originalFetch.apply(this, args);
      
      try {
        const url = typeof args[0] === 'string' ? args[0] : args[0].url;
        if (url && (url.includes('checkout') || url.includes('order') || url.includes('purchase') || url.includes('thank')) && !url.includes('/cart/add') && !url.includes('/cart/update') && !url.includes('/cart.js')) {
          const clonedResponse = response.clone();
          const text = await clonedResponse.text();
          
          let orderData = null;
          try {
            const parsed = JSON.parse(text);
            if (parsed && (parsed.order || parsed.checkout || parsed.line_items || parsed.items)) {
              orderData = parsed.order || parsed.checkout || parsed;
            }
          } catch (e) {}
          
          if (orderData) {
            window.shopifyIntegrations?.trackComprehensivePurchase(orderData);
          }
        }
      } catch (e) {
        console.warn('Error checking fetch for order data:', e);
      }
      
      return response;
    };
  }
  
  monitorLocalStorageChanges() {
    // Override localStorage methods to catch order data
    const originalSetItem = localStorage.setItem;
    
    localStorage.setItem = function(key, value) {
      if (key && (key.includes('order') || key.includes('checkout') || key.includes('purchase')) && !key.includes('cart')) {
        try {
          const data = JSON.parse(value);
          if (data && (data.line_items || data.items || data.order_number)) {
            setTimeout(() => {
              window.shopifyIntegrations?.trackComprehensivePurchase(data);
            }, 100);
          }
        } catch (e) {}
      }
      return originalSetItem.call(this, key, value);
    };
    
    // Also check existing localStorage data
    Object.keys(localStorage).forEach(key => {
      if ((key.includes('order') || key.includes('checkout') || key.includes('purchase')) && !key.includes('cart')) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data && (data.line_items || data.items || data.order_number)) {
            this.trackComprehensivePurchase(data);
          }
        } catch (e) {}
      }
    });
  }
  
  trackAllOrderForms() {
    // Listen to all form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      const action = form.action || '';
      
      if (action.includes('checkout') || action.includes('order') || form.querySelector('input[name*="order"]')) {
        setTimeout(() => {
          // Try to capture order data after form submission
          this.captureOrderDataFromPage();
        }, 1000);
      }
    }, true);
    
    // Listen to all button clicks that might trigger purchases
    document.addEventListener('click', (e) => {
      const button = e.target.closest('button, input[type="submit"], a');
      if (button) {
        const text = button.textContent || button.value || '';
        const classes = button.className || '';
        
        if ((text.toLowerCase().includes('complete') && text.toLowerCase().includes('order')) ||
            (text.toLowerCase().includes('place') && text.toLowerCase().includes('order')) ||
            classes.includes('checkout-button') ||
            classes.includes('complete-order')) {
          
          setTimeout(() => {
            this.captureOrderDataFromPage();
          }, 2000);
        }
      }
    }, true);
  }
  
  monitorDOMForOrderData() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if new element contains order data
              const element = node;
              if (element.classList && (
                element.classList.contains('order-confirmation') ||
                element.classList.contains('thank-you') ||
                element.classList.contains('order-details') ||
                element.querySelector && element.querySelector('.order-number, .order-total, .line-item')
              )) {
                setTimeout(() => {
                  this.captureOrderDataFromPage();
                }, 500);
              }
            }
          });
        }
        
        // Check for attribute changes that might indicate order completion
        if (mutation.type === 'attributes') {
          const element = mutation.target;
          if (element.classList.contains('checkout-complete') || 
              element.dataset.orderComplete || 
              element.getAttribute('data-order-id')) {
            setTimeout(() => {
              this.captureOrderDataFromPage();
            }, 500);
          }
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'data-order-id', 'data-order-complete']
    });
  }
  
  startLimitedPurchaseMonitoring() {
    let monitoringCount = 0;
    const maxMonitoring = 5; // Only 5 attempts (10 seconds)
    
    // Check for order data every 2 seconds for 10 seconds only
    const intervalId = setInterval(() => {
      monitoringCount++;
      
      // Only capture once per monitoring session
      if (monitoringCount === 1) {
        this.captureOrderDataFromPage();
      }
      
      // Stop monitoring after 10 seconds
      if (monitoringCount >= maxMonitoring) {
        clearInterval(intervalId);
        this.log('Limited purchase monitoring stopped after 10 seconds');
      }
    }, 2000);
  }
  
  monitorBrowserEventsForPurchase() {
    // Listen for various events that might indicate purchase completion
    const events = ['pageshow', 'focus', 'visibilitychange', 'beforeunload'];
    
    events.forEach(eventType => {
      window.addEventListener(eventType, () => {
        if (this.isThankYouPage() || this.isOrderConfirmationPage()) {
          setTimeout(() => {
            this.captureOrderDataFromPage();
          }, 100);
        }
      });
    });
    
    // Listen for hash changes
    window.addEventListener('hashchange', () => {
      if (location.hash.includes('order') || location.hash.includes('thank')) {
        setTimeout(() => {
          this.captureOrderDataFromPage();
        }, 500);
      }
    });
  }
  
  captureOrderDataFromPage() {
    try {
      // Multiple methods to capture order data
      let orderData = this.getOrderFromThankYouPage() ||
                     this.getOrderFromShopifyAnalytics() ||
                     this.getOrderFromLocalStorage() ||
                     this.getOrderFromGlobalObjects() ||
                     this.getOrderFromPageContent();
      
      if (orderData && orderData.items && orderData.items.length > 0) {
        this.trackComprehensivePurchase(orderData);
      }
    } catch (e) {
      console.warn('Error capturing order data from page:', e);
    }
  }
  
  getOrderFromGlobalObjects() {
    // Check various global objects for order data
    const globalSources = [
      'window.order',
      'window.checkout',
      'window.orderData',
      'window.checkoutData',
      'window.Shopify.checkout',
      'window.ShopifyAnalytics.meta.order',
      'window.Checkout.order'
    ];
    
    for (const source of globalSources) {
      try {
        const parts = source.split('.');
        let obj = window;
        for (let i = 1; i < parts.length; i++) {
          obj = obj[parts[i]];
          if (!obj) break;
        }
        
        if (obj && (obj.line_items || obj.items || obj.order_number)) {
          return this.normalizeOrderData(obj);
        }
      } catch (e) {}
    }
    
    return null;
  }
  
  getOrderFromPageContent() {
    // Extract order data from page content as last resort
    const orderData = {};
    
    // Try to find order number in various formats
    const orderPatterns = [
      /order[^\d]*(\d+)/i,
      /#(\d{4,})/,
      /confirmation[^\d]*(\d+)/i
    ];
    
    const pageText = document.body.textContent;
    for (const pattern of orderPatterns) {
      const match = pageText.match(pattern);
      if (match) {
        orderData.id = match[1];
        break;
      }
    }
    
    // Extract total price
    const priceElements = document.querySelectorAll('[class*="total"], [class*="price"], [data-price]');
    for (const element of priceElements) {
      const text = element.textContent;
      const price = this.extractPriceFromText(text);
      if (price > 0) {
        orderData.total_price = price;
        break;
      }
    }
    
    // Extract items
    const itemElements = document.querySelectorAll('[class*="item"], [class*="product"], [data-product]');
    if (itemElements.length > 0) {
      orderData.items = Array.from(itemElements).map(el => ({
        title: el.querySelector('[class*="name"], [class*="title"]')?.textContent?.trim() || 'Product',
        price: this.extractPriceFromText(el.textContent) || 0,
        quantity: 1
      })).filter(item => item.title !== 'Product' || item.price > 0);
    }
    
    return Object.keys(orderData).length > 0 ? this.normalizeOrderData(orderData) : null;
  }
  
  createOrderId(orderData) {
    const id = orderData.transaction_id || 'unknown';
    const value = orderData.value || 0;
    const timestamp = orderData.timestamp || Date.now();
    
    return `${id}_${value}_${timestamp}`.replace(/[^a-zA-Z0-9_]/g, '_');
  }
  
  log(message) {
    if (window.location.search.includes('debug=true')) {
      console.log(`[Shopify Integrations] ${message}`);
    }
  }
  
  checkGlobalObjectsForOrderData() {
    // Continuously check for new order data in global objects
    const checks = [
      () => window.Shopify?.checkout,
      () => window.ShopifyAnalytics?.meta?.order,
      () => window.order,
      () => window.orderData,
      () => window.checkout,
      () => window.checkoutData
    ];
    
    checks.forEach(check => {
      try {
        const data = check();
        if (data && (data.line_items || data.items || data.order_number)) {
          // Use our own tracking instead of modifying the original data
          this.trackComprehensivePurchase(data);
        }
      } catch (e) {}
    });
  }
  
  isOrderConfirmationPage() {
    const path = window.location.pathname;
    const title = document.title.toLowerCase();
    
    return document.querySelector('.order-confirmation, .checkout-confirmation, [data-order-confirmation]') !== null ||
           (title.includes('order') && title.includes('confirmation')) ||
           (title.includes('thank') && title.includes('you')) ||
           window.location.search.includes('order_id') ||
           window.location.search.includes('checkout_id') ||
           (path.includes('/orders/') && !path.includes('/account/orders'));
  }
}

// Auto-initialize when data layer is ready
document.addEventListener('DOMContentLoaded', () => {
  if (window.shopifyDataLayer) {
    window.shopifyIntegrations = new ShopifyIntegrations(window.shopifyDataLayer);
  } else {
    // Wait for data layer to be initialized
    const checkDataLayer = setInterval(() => {
      if (window.shopifyDataLayer) {
        window.shopifyIntegrations = new ShopifyIntegrations(window.shopifyDataLayer);
        clearInterval(checkDataLayer);
      }
    }, 100);
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ShopifyIntegrations;
}